import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, Link } from 'react-router-dom';
import { Button, Form, Badge, Alert } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { useHelp } from './HelpProvider';
import { helpAllowed, normalize, pathMatches, roleAllowed, searchFields, resolveSearch } from './helpCore';
import { searchJobData } from './jobSearch';
import { fetchBackgroundData } from '../redux/api/portalApi';
import index from './fieldIndex.generated.json';
import Markdown from './Markdown';
import AssistantChat from './AssistantChat';
import './help.css';

const EMPTY = [];
function sourceAllowed(field, canView) {
  const area = field.source.split('/')[2];
  const permission = {
    AdminPage: 'admin.container', AppsPage: 'home.apps-portal', JobDetailPage: 'home.production-portal',
    MainPage: 'home.production-portal', AssemblyPage: 'home.assembly', ShippingPage: 'home.shipping',
    JobSchedulePage: 'home.production-schedule', SalesPortal: 'home.sales-portal',
    EngineeringPage: 'home.engineering-tools', FormsPage: 'home.forms', PanelShopPage: 'home.panel-shop',
    UserPage: 'home.user-home', ControlsPage: 'home.controls',
  }[area];
  return permission ? canView(permission) : false;
}
export default function HelpDrawer() {
  const { config } = useHelp();
  const { pathname } = useLocation();
  const { isAuthenticated } = useAuth();
  const { canView, isLoading } = usePermissions();
  const mapped = useSelector((s) => s.user.rolesMapped);
  const status = useSelector((s) => s.user.status);
  const account = useSelector((s) => s.user.userDetails?.localAccountId);
  const environment = useSelector((s) => s.environment?.environment);
  const apiScope = useSelector((s) => JSON.stringify(s.apiUrls.urls));
  const permissionScope = useSelector((s) => JSON.stringify(s.permissions?.computedPermissions || {}));
  const roles = Object.keys(mapped || {}).filter((key) => mapped[key]);
  // Hard rollout boundary: configuration may narrow this access, never broaden it.
  if (!isAuthenticated || isLoading || status !== 'succeeded' || mapped?.Admin !== true ||
    !pathMatches('/admin/*', pathname) || !canView('admin.container') ||
    !helpAllowed(config, pathname, roles)) return null;
  return <SearchDrawer key={`${account}:${environment}:${apiScope}:${permissionScope}:${pathname}:${roles.join(',')}:${JSON.stringify(config)}`}
    config={config} pathname={pathname} roles={roles} canView={canView} />;
}

export function SearchDrawer({ config, pathname, roles, canView }) {
  const [open, setOpen] = useState(false);
  const [session, setSession] = useState(false);
  const launcher = useRef(null);
  const panel = useRef(null);
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open) panel.current?.focus();
    else if (wasOpen.current) launcher.current?.focus();
    wasOpen.current = open;
  }, [open]);
  const minimize = () => {
    request.current?.abort(); setBusy(false); setOpen(false); launcher.current?.focus();
  };
  const [tab, setTab] = useState('ai');
  const [query, setQuery] = useState('');
  const [mode, setMode] = useState('auto');
  const [turns, setTurns] = useState([]);
  const [busy, setBusy] = useState(false);
  const request = useRef(null);
  const dispatch = useDispatch();
  const urls = useSelector((s) => s.apiUrls.urls);
  useEffect(() => () => request.current?.abort(), []);
  const options = useSelector((s) => s.dropdown?.options || EMPTY);
  const admin = canView('admin.help-manager');
  const submit = async (event) => {
    event.preventDefault();
    if (!query.trim() || busy) return;
    const resolved = resolveSearch(query, mode, pathname);
    const terms = normalize(resolved.term).split(' ').filter(Boolean);
    let values = [];
    let coverage = null;
    if (resolved.mode === 'job') {
      if (!canView(resolved.jobId ? 'home.production-portal' : 'home.global-search')) {
        resolved.error = resolved.jobId ? 'You do not have access to job search.' : 'All-jobs search requires Global Search access. Ask an administrator.';
      } else if (!terms.length) {
        resolved.error = 'Enter a field name or value to search. This version searches keywords; it does not calculate totals or interpret scheduling questions.';
      } else {
        const controller = new AbortController();
        request.current = controller;
        setBusy(true);
        try {
          coverage = await searchJobData({ urls, ...resolved,
            load: (url, signal) => dispatch(fetchBackgroundData({ url, signal })), signal: controller.signal });
          values = coverage.matches;
        } catch (e) {
          resolved.error = e.message || 'The job search could not be completed. Try again.';
        } finally {
          if (!controller.signal.aborted) setBusy(false);
        }
        if (controller.signal.aborted) return;
      }
    }
    const fields = resolved.mode === 'fields' ? searchFields(index.fields.filter((f) =>
      sourceAllowed(f, canView) && (!f.route || helpAllowed(config, f.route, roles))), options, resolved.term) : [];
    const articles = resolved.mode === 'fields' ? config.articles.filter((a) =>
      roleAllowed(a.roles, roles) && pathMatches(a.page, pathname) &&
      terms.length && terms.every((t) => normalize(`${a.title} ${a.markdown}`).includes(t))) : [];
    setTurns((previous) => [...previous.slice(-9), {
      query: query.trim(), ...resolved, fields: fields.slice(0, 30), total: fields.length, values, articles, coverage,
      time: new Date().toLocaleTimeString(),
    }]);
    setQuery('');
  };
  return <div data-portal-help>
    <Button ref={launcher} hidden={open} className="portal-help-launcher" onClick={() => { setSession(true); setOpen(true); }} aria-label="Open portal help" aria-expanded={open}>✦ {session ? 'Resume chat' : 'Assistant'}</Button>
    {session && <section ref={panel} tabIndex={-1} hidden={!open} role="dialog" aria-modal="false" className="portal-assistant-window" aria-labelledby="portal-assistant-title" data-portal-help onKeyDown={e => {
      if (e.key === 'Escape') { e.stopPropagation(); minimize(); }
    }}>
      <header className="portal-assistant-header">
        <Button variant="link" onClick={minimize} aria-label="Minimize chat"><span aria-hidden="true">⌄</span></Button>
        <h2 id="portal-assistant-title">Portal assistant</h2>
        <Button variant="link" onClick={() => { minimize(); setSession(false); setTurns([]); setQuery(''); setTab('ai'); }}>End Chat</Button>
      </header>
      <div className="portal-assistant-body">
        <div className="portal-assistant-modes" role="group" aria-label="Help mode">
          <Button variant={tab === 'search' ? 'primary' : 'outline-primary'} aria-pressed={tab === 'search'} onClick={() => setTab('search')}>Field search</Button>
          <Button variant={tab === 'ai' ? 'primary' : 'outline-primary'} aria-pressed={tab === 'ai'} onClick={() => {
            request.current?.abort(); setBusy(false); setTab('ai');
          }}>Ask AI</Button>
        </div>
        {tab === 'ai' ? <AssistantChat active={open} /> : <div className="portal-assistant-search">
        <div className="portal-help-intro">
          <Badge bg="light" text="dark">Field directory · Keyword search</Badge>
          <p className="mt-2 mb-1">Find a field, explore dropdown choices, or search saved job values.</p>
          <p className="mb-1"><strong>Job search scope: {pathname.match(/^\/(\d+)(?:\/|$)/) ? `Open job (ID ${pathname.split('/')[1]})` : 'All accessible jobs'}</strong></p>
          <small>Keyword search, not AI answers. Use “where” for field locations, or choose a search mode below.</small>
          <small className="text-muted">Page: {pathname}</small>
          {admin && <p className="mt-2 mb-0"><Link to="/admin/help" onClick={() => setOpen(false)}>Manage help content and visibility</Link></p>}
        </div>
        {!turns.length && <div className="mt-3">
          <h6>Try a search</h6>
          {['heat', 'Where does enclosure cooling go?', 'enclosure cooling'].map((q) =>
            <Button key={q} variant="outline-secondary" size="sm" className="me-2 mb-2" onClick={() => setQuery(q)}>{q}</Button>)}
          {config.articles.filter((a) => a.id === 'getting-started' && roleAllowed(a.roles, roles) && pathMatches(a.page, pathname))
            .map((a) => <details key={a.id}><summary>{a.title}</summary><Markdown text={a.markdown} /></details>)}
        </div>}
        <div className="portal-help-conversation" aria-live="polite">
          {turns.map((turn, i) => <section key={i} className="portal-help-turn">
            <p className="portal-help-question">{turn.query}</p>
            <Badge bg={turn.mode === 'job' ? 'success' : 'primary'}>{turn.mode === 'job' ? (turn.jobId ? `Saved values · job ID ${turn.jobId}` : 'Saved values · all accessible jobs') : 'Field locations & available choices'}</Badge>
            {turn.mode === 'job' ? <>
              <p className="small text-muted mt-2">API snapshot · {turn.time} · Saved values only; unsaved edits are excluded.</p>
              {turn.coverage && <p className="small">{turn.coverage.total} matching fields in returned records across {turn.coverage.searchedSections} sections.
                {turn.coverage.total > 100 && ' Showing the first 100; refine your search.'} Results are not a count of jobs.</p>}
              {!!turn.coverage?.failed.length && <Alert variant="warning">Partial coverage: {turn.coverage.failed.join(', ')} could not be searched. Missing results must not be treated as absent values.</Alert>}
              {turn.coverage && !turn.jobId && <p className="small text-muted">Coverage is limited to records returned by the existing list APIs. A server-side search endpoint is needed to guarantee complete results at scale.</p>}
              {turn.error ? <Alert variant="info">{turn.error}</Alert> : turn.values.length ?
                turn.values.map((v, j) => <div className="portal-help-result" key={j}>
                  <Link to={`/${encodeURIComponent(v.jobId)}/generalInfo`} onClick={() => setOpen(false)}>Job {v.jobNumber}</Link>
                  <p className="mb-1"><strong>{v.label}</strong> · {v.section}{v.zone != null ? ` · Zone ${v.zone}` : ''}</p><p>{v.value}</p></div>) :
                <p>No matching saved values were returned. Try a field name or shorter term and check the coverage notice.</p>}
            </> : <>
              {!options.length && <Alert variant="info" className="mt-2">Dropdown choices are not loaded yet. Field names and help articles are still searchable.</Alert>}
              <p className="small text-muted mt-2">{turn.total} field occurrences{turn.total > 30 ? ' · showing the first 30; refine your search' : ''}. Source-derived locations may need verification.</p>
              {!turn.total && !turn.articles.length && <p>No matches. Try a shorter field name or dropdown choice.</p>}
              {turn.articles.map((a) => <details key={a.id} className="portal-help-result"><summary>{a.title}</summary><Markdown text={a.markdown} /></details>)}
              {turn.fields.map((f) => <article className="portal-help-result" key={f.id}>
                <strong>{f.label}</strong>
                <p className="small mb-1">{f.area} → {f.component}</p>
                {!!f.matchedChoices.length && <p className="mb-1"><span className="text-muted">Available choices: </span>{f.matchedChoices.join(', ')}</p>}
                {!!f.fields.length && <small>Field: {f.fields.join(', ')}</small>}
                {admin && <details className="small mt-1"><summary>Source & API references</summary>
                  <p>{f.source}:{f.line}</p>
                  <p>Component API keys: {f.apiKeys.join(', ') || 'Unresolved'}. These are not verified field write endpoints.</p>
                </details>}
              </article>)}
            </>}
          </section>)}
        </div>
        <Form onSubmit={submit} className="portal-help-composer">
          <Form.Group controlId="portal-help-mode" className="mb-2">
            <Form.Label>Search in</Form.Label>
            <Form.Select value={mode} disabled={busy} onChange={(e) => setMode(e.target.value)}>
              <option value="auto">Choose from my question</option>
              <option value="fields">Field locations & available choices</option>
              <option value="job">Saved job values (scope follows the URL)</option>
            </Form.Select>
          </Form.Group>
          <Form.Group controlId="portal-help-query">
            <Form.Label>Question or search term</Form.Label>
            <Form.Control as="textarea" rows={2} maxLength={500} value={query} disabled={busy}
              placeholder="Search for heat, enclosure cooling, a field…"
              onChange={(e) => setQuery(e.target.value)} />
          </Form.Group>
          <div className="d-flex gap-2 mt-2">
            <Button type="submit" disabled={!query.trim() || busy}>{busy ? 'Searching job APIs…' : 'Search'}</Button>
            {busy && <Button variant="outline-secondary" onClick={() => { request.current?.abort(); setBusy(false); }}>Cancel</Button>}
            <Button variant="outline-secondary" onClick={() => setTurns([])} disabled={!turns.length}>Clear conversation</Button>
          </div>
        </Form>
        </div>}
      </div>
    </section>}
  </div>;
}
